import { Badge, Button, Icon, Modal } from "../../components/design";
import useTranslator from "../../hooks/useTranslator";
import styles from "./ApplicationCreatedModal.module.css";
import sm from "./ApplicationPendingModal.module.css";
import ApplicationPeopleSummary, {
  ApplicationMember,
} from "./ApplicationPeopleSummary";

type Props = {
  title: string;
  peopleCount: number;
  members?: ApplicationMember[];
  pendingCount: number;
  onProceed: () => void;
  onLater: () => void;
};

export default function ApplicationPendingModal({
  title,
  peopleCount,
  members,
  pendingCount,
  onProceed,
  onLater,
}: Props) {
  const { _t } = useTranslator();
  return (
    <Modal
      open
      onClose={onLater}
      dismissible={false}
      size="wide"
      icon={
        <span className={styles.check}>
          <Icon name="check" size={28} />
        </span>
      }
      title={_t("onboarding_application_created_title", "Application created")}
      description={title}
      actionsLayout="stack"
      actions={
        <>
          <Button
            size="lg"
            block
            onClick={onProceed}
            iconEnd={
              <Badge tone="accent">
                {_t(
                  "onboarding_application_created_pending_badge",
                  "{{count}} pending",
                  "translation",
                  { count: pendingCount }
                )}
              </Badge>
            }
          >
            {_t(
              "onboarding_application_created_complete_pending",
              "Complete pending profiles"
            )}
          </Button>
          <p className={sm.later}>
            {_t(
              "onboarding_application_created_later_prefix",
              "Or do it later from your"
            )}{" "}
            <Button variant="link" size="sm" onClick={onLater}>
              {_t("onboarding_application_created_dashboard_link", "dashboard")}
            </Button>
          </p>
        </>
      }
    >
      <p className={styles.subtitle}>
        {_t(
          "onboarding_application_created_for_people",
          "You're making the application for {{count}} people",
          "translation",
          { count: peopleCount }
        )}
      </p>
      <ApplicationPeopleSummary members={members} />
      <div className={sm.whatNext}>
        <strong>{_t("onboarding_what_next_title", "What happens next")}</strong>
        <div className={sm.row}>
          <Icon name="send" size={16} className={sm.ico} />
          <div>
            <b>
              {_t("onboarding_what_next_invited_title", "Invited profiles")}
            </b>
            <p>
              {_t(
                "onboarding_what_next_invited_desc",
                "We'll email them automatically. They complete their own application."
              )}
            </p>
          </div>
        </div>
        <div className={sm.row}>
          <Icon name="idCard" size={16} className={sm.ico} />
          <div>
            <b>
              {_t(
                "onboarding_what_next_self_managed_title",
                "Profiles you'll fill in"
              )}
            </b>
            <p>
              {_t(
                "onboarding_what_next_self_managed_need",
                "You'll need, for each person:"
              )}
            </p>
            <div className={sm.need}>
              <Icon name="idCard" size={16} />
              <span>
                {_t("onboarding_what_next_need_id", "Their ID or passport")}
              </span>
            </div>
            <div className={sm.need}>
              <Icon name="briefcase" size={16} />
              <span>
                {_t(
                  "onboarding_what_next_need_income",
                  "Proof of work and income (payslips, contract or invoices)"
                )}
              </span>
            </div>
            <div className={sm.info}>
              <Icon name="info" size={16} />
              <span>
                {_t(
                  "onboarding_what_next_consent_info",
                  "When you complete a profile, we'll email that person to confirm you had their permission, review their details, and accept the terms before we process their information."
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
